import useSWR from 'swr'

import { MutationOptions, useCallMutation } from '@/common/api/client'
import { apiDeleteFetcher, apiPutFetcher } from '@/common/api/fetcher'
import {
  AddSynonymVars,
  ApproveCandidateVars,
  CandidateActionVars,
  CreateTagCandidateVars,
  DecideTagAbVars,
  DeleteSynonymVars,
  ExtractCandidatesResponse,
  ExtractCandidatesVars,
  GetTagDashboardVars,
  GetTagDeliveryVars,
  GetTagSettingsVars,
  GetTagVars,
  IssueTagTokenResponse,
  ListTagCandidatesResponse,
  ListTagCandidatesVars,
  ListTagOptimizationsResponse,
  ListTagOptimizationsVars,
  ListTagsResponse,
  ListTagsVars,
  PreviewCandidateResponse,
  RebuildTagResponse,
  RunTagOptimizationsResponse,
  RunTagOptimizationsVars,
  SearchTag,
  SearchTagApiPath,
  StartTagAbVars,
  TagAbActionVars,
  TagCandidate,
  TagDashboardResponse,
  TagDeliveryResponse,
  TagDetailResponse,
  TagOptimizationActionResponse,
  TagOptimizationActionVars,
  TagSettingsResponse,
  UpdateTagDeliveryVars,
  UpdateTagSettingsVars,
  UpdateTagVars
} from '@/common/api/params/searchTag'

// --- 承認キュー ---

export const useTagCandidateListApi = (vars: ListTagCandidatesVars | undefined) => {
  return useSWR<ListTagCandidatesResponse, Response, [string, ListTagCandidatesVars] | undefined>(
    vars ? [SearchTagApiPath.LIST_CANDIDATES, vars] : undefined
  )
}

export const useTagCandidateCreateApi = (options?: MutationOptions<TagCandidate>) => {
  return useCallMutation<TagCandidate, CreateTagCandidateVars>(
    SearchTagApiPath.CREATE_CANDIDATE,
    options
  )
}

export const useTagCandidatePreviewApi = (options?: MutationOptions<PreviewCandidateResponse>) => {
  return useCallMutation<PreviewCandidateResponse, CandidateActionVars>(
    SearchTagApiPath.PREVIEW_CANDIDATE,
    options
  )
}

export const useTagCandidateApproveApi = (options?: MutationOptions<TagDetailResponse>) => {
  return useCallMutation<TagDetailResponse, ApproveCandidateVars>(
    SearchTagApiPath.APPROVE_CANDIDATE,
    options
  )
}

export const useTagCandidateRejectApi = (options?: MutationOptions<TagCandidate>) => {
  return useCallMutation<TagCandidate, CandidateActionVars>(
    SearchTagApiPath.REJECT_CANDIDATE,
    options
  )
}

export const useTagCandidateHoldApi = (options?: MutationOptions<TagCandidate>) => {
  return useCallMutation<TagCandidate, CandidateActionVars>(
    SearchTagApiPath.HOLD_CANDIDATE,
    options
  )
}

export const useTagCandidateExtractApi = (options?: MutationOptions<ExtractCandidatesResponse>) => {
  return useCallMutation<ExtractCandidatesResponse, ExtractCandidatesVars>(
    SearchTagApiPath.EXTRACT_CANDIDATES,
    options
  )
}

// --- タグ一覧・詳細 ---

export const useTagListApi = (vars: ListTagsVars | undefined) => {
  return useSWR<ListTagsResponse, Response, [string, ListTagsVars] | undefined>(
    vars ? [SearchTagApiPath.LIST_TAGS, vars] : undefined
  )
}

export const useTagDetailApi = (vars: GetTagVars | undefined) => {
  return useSWR<TagDetailResponse, Response, [string, GetTagVars] | undefined>(
    vars ? [SearchTagApiPath.GET_TAG, vars] : undefined
  )
}

export const useTagUpdateApi = (options?: MutationOptions<SearchTag>) => {
  return useCallMutation<SearchTag, UpdateTagVars>(
    SearchTagApiPath.UPDATE_TAG,
    options,
    apiPutFetcher
  )
}

export const useTagRebuildApi = (options?: MutationOptions<RebuildTagResponse>) => {
  return useCallMutation<RebuildTagResponse, GetTagVars>(SearchTagApiPath.REBUILD_TAG, options)
}

export const useTagSynonymAddApi = (options?: MutationOptions<TagDetailResponse>) => {
  return useCallMutation<TagDetailResponse, AddSynonymVars>(SearchTagApiPath.ADD_SYNONYM, options)
}

export const useTagSynonymDeleteApi = (options?: MutationOptions<TagDetailResponse>) => {
  return useCallMutation<TagDetailResponse, DeleteSynonymVars>(
    SearchTagApiPath.DELETE_SYNONYM,
    options,
    apiDeleteFetcher
  )
}

// --- 文言A/B ---

export const useTagAbStartApi = (options?: MutationOptions<TagDetailResponse>) => {
  return useCallMutation<TagDetailResponse, StartTagAbVars>(SearchTagApiPath.START_AB, options)
}

export const useTagAbStopApi = (options?: MutationOptions<TagDetailResponse>) => {
  return useCallMutation<TagDetailResponse, TagAbActionVars>(SearchTagApiPath.STOP_AB, options)
}

export const useTagAbDecideApi = (options?: MutationOptions<TagDetailResponse>) => {
  return useCallMutation<TagDetailResponse, DecideTagAbVars>(SearchTagApiPath.DECIDE_AB, options)
}

// --- 設定 ---

export const useTagSettingsGetApi = (vars: GetTagSettingsVars | undefined) => {
  return useSWR<TagSettingsResponse, Response, [string, GetTagSettingsVars] | undefined>(
    vars ? [SearchTagApiPath.GET_SETTINGS, vars] : undefined
  )
}

export const useTagSettingsUpdateApi = (options?: MutationOptions<TagSettingsResponse>) => {
  return useCallMutation<TagSettingsResponse, UpdateTagSettingsVars>(
    SearchTagApiPath.UPDATE_SETTINGS,
    options,
    apiPutFetcher
  )
}

// --- ダッシュボード ---

export const useTagDashboardApi = (vars: GetTagDashboardVars | undefined) => {
  return useSWR<TagDashboardResponse, Response, [string, GetTagDashboardVars] | undefined>(
    vars ? [SearchTagApiPath.GET_DASHBOARD, vars] : undefined
  )
}

// --- 最適化提案 ---

export const useTagOptimizationListApi = (vars: ListTagOptimizationsVars | undefined) => {
  return useSWR<
    ListTagOptimizationsResponse,
    Response,
    [string, ListTagOptimizationsVars] | undefined
  >(vars ? [SearchTagApiPath.LIST_OPTIMIZATIONS, vars] : undefined)
}

export const useTagOptimizationRunApi = (
  options?: MutationOptions<RunTagOptimizationsResponse>
) => {
  return useCallMutation<RunTagOptimizationsResponse, RunTagOptimizationsVars>(
    SearchTagApiPath.RUN_OPTIMIZATIONS,
    options
  )
}

export const useTagOptimizationApplyApi = (
  options?: MutationOptions<TagOptimizationActionResponse>
) => {
  return useCallMutation<TagOptimizationActionResponse, TagOptimizationActionVars>(
    SearchTagApiPath.APPLY_OPTIMIZATION,
    options
  )
}

export const useTagOptimizationDismissApi = (
  options?: MutationOptions<TagOptimizationActionResponse>
) => {
  return useCallMutation<TagOptimizationActionResponse, TagOptimizationActionVars>(
    SearchTagApiPath.DISMISS_OPTIMIZATION,
    options
  )
}

// --- タグ配信 ---

export const useTagDeliveryGetApi = (vars: GetTagDeliveryVars | undefined) => {
  return useSWR<TagDeliveryResponse, Response, [string, GetTagDeliveryVars] | undefined>(
    vars ? [SearchTagApiPath.GET_DELIVERY, vars] : undefined
  )
}

export const useTagDeliveryUpdateApi = (options?: MutationOptions<TagDeliveryResponse>) => {
  return useCallMutation<TagDeliveryResponse, UpdateTagDeliveryVars>(
    SearchTagApiPath.UPDATE_DELIVERY,
    options,
    apiPutFetcher
  )
}

export const useTagDeliveryTokenIssueApi = (options?: MutationOptions<IssueTagTokenResponse>) => {
  return useCallMutation<IssueTagTokenResponse, GetTagDeliveryVars>(
    SearchTagApiPath.ISSUE_DELIVERY_TOKEN,
    options
  )
}
